<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import TeamPresenceAPI from 'dashboard/api/teletel/teamPresence';
import NextButton from 'dashboard/components-next/button/Button.vue';

// Global summon dialog (owner order 2026-09-26). A summon arrives only on
// this agent's own pubsub token and stays on screen, ringing, until the
// agent presses "Responding". Summons queue by id, so a second one never
// hides the first; a summon answered by a colleague or escalated closes on
// every target's screen. Escalations reach the sender and the chosen
// supervisor the same way, with a plain OK.
const RING_LIMIT = 60000;

const { t } = useI18n();
const queue = ref([]);
const responding = ref(false);
const soundBlocked = ref(false);
let audio = null;
let ringTimer = null;

const current = computed(() => queue.value[0] || null);

const stopRing = () => {
  clearTimeout(ringTimer);
  if (audio) audio.pause();
  audio = null;
};

const ring = () => {
  stopRing();
  audio = new Audio('/audio/dashboard/ringtone.mp3');
  audio.loop = true;
  soundBlocked.value = false;
  audio.play().catch(() => {
    soundBlocked.value = true;
  });
  ringTimer = setTimeout(stopRing, RING_LIMIT);
};

const title = computed(() => {
  if (!current.value) return '';
  if (current.value.kind === 'summon')
    return t('TELETEL_TEAM_PRESENCE.DIALOG.TITLE', {
      name: current.value.sender_name,
    });
  return t('TELETEL_TEAM_PRESENCE.DIALOG.ESCALATED_SUPERVISOR_TITLE');
});

const body = computed(() => {
  const data = current.value;
  if (!data) return '';
  if (data.kind === 'summon')
    return data.note || t('TELETEL_TEAM_PRESENCE.DIALOG.NO_NOTE');
  const names = (data.target_names || []).join('، ');
  if (data.recipient === 'sender')
    return t('TELETEL_TEAM_PRESENCE.DIALOG.ESCALATED_SENDER', { names });
  return t('TELETEL_TEAM_PRESENCE.DIALOG.ESCALATED_SUPERVISOR', {
    sender: data.sender_name,
    names,
    note: data.note || '—',
  });
});

const push = item => {
  const key = `${item.kind}:${item.id}`;
  if (queue.value.some(q => `${q.kind}:${q.id}` === key)) return;
  queue.value = [...queue.value, item];
  ring();
};

const drop = id => {
  const wasShown = current.value?.kind === 'summon' && current.value.id === id;
  queue.value = queue.value.filter(q => !(q.kind === 'summon' && q.id === id));
  if (!queue.value.length) stopRing();
  else if (wasShown) ring();
};

const onSummon = data => push({ ...data, kind: 'summon' });
const onEscalated = data => push({ ...data, kind: 'escalated' });

const onAcknowledged = data => {
  useAlert(
    t('TELETEL_TEAM_PRESENCE.DIALOG.ACKNOWLEDGED', {
      name: data.responder_name,
    })
  );
};

// Answered by a colleague, or escalated: stop ringing here too.
const onClosed = data => {
  if (!queue.value.some(q => q.kind === 'summon' && q.id === data.id)) return;
  // This agent's own answer is in flight: respond() reports it.
  if (responding.value && current.value?.id === data.id) return;
  drop(data.id);
  if (data.state === 'taken')
    useAlert(
      t('TELETEL_TEAM_PRESENCE.DIALOG.TAKEN_BY', { name: data.responder_name })
    );
};

const close = () => {
  queue.value = queue.value.slice(1);
  if (queue.value.length) ring();
  else stopRing();
};

const respond = async () => {
  responding.value = true;
  const summon = current.value;
  try {
    const { data } = await TeamPresenceAPI.acknowledge(summon.id);
    useAlert(
      data.acknowledged
        ? t('TELETEL_TEAM_PRESENCE.DIALOG.RESPONDED', {
            name: summon.sender_name,
          })
        : t('TELETEL_TEAM_PRESENCE.DIALOG.TAKEN')
    );
    drop(summon.id);
  } catch {
    // Keep the dialog: the agent must know the answer did not reach anyone.
    useAlert(t('TELETEL_TEAM_PRESENCE.DIALOG.RESPOND_FAILED'));
  } finally {
    responding.value = false;
  }
};

onMounted(() => {
  emitter.on('teletelSummon', onSummon);
  emitter.on('teletelSummonEscalated', onEscalated);
  emitter.on('teletelSummonAcknowledged', onAcknowledged);
  emitter.on('teletelSummonClosed', onClosed);
});
onBeforeUnmount(() => {
  emitter.off('teletelSummon', onSummon);
  emitter.off('teletelSummonEscalated', onEscalated);
  emitter.off('teletelSummonAcknowledged', onAcknowledged);
  emitter.off('teletelSummonClosed', onClosed);
  stopRing();
});
</script>

<template>
  <div
    v-if="current"
    class="fixed inset-0 z-[9999] flex items-center justify-center bg-n-alpha-black1 p-4"
  >
    <div
      class="flex w-full max-w-md flex-col gap-4 rounded-2xl border border-n-ruby-7 bg-n-solid-1 p-6 shadow-xl"
    >
      <div class="flex items-center gap-3">
        <span
          class="i-lucide-bell-ring size-7 shrink-0 animate-pulse text-n-ruby-10"
        />
        <h2 class="text-lg font-semibold text-n-slate-12">{{ title }}</h2>
        <span
          v-if="queue.length > 1"
          class="ms-auto rounded-full bg-n-ruby-3 px-2 text-xs text-n-ruby-11"
        >
          {{
            t('TELETEL_TEAM_PRESENCE.DIALOG.MORE', { count: queue.length - 1 })
          }}
        </span>
      </div>
      <p class="whitespace-pre-line text-base text-n-slate-12">{{ body }}</p>
      <p v-if="soundBlocked" class="text-xs text-n-amber-11">
        {{ t('TELETEL_TEAM_PRESENCE.DIALOG.SOUND_BLOCKED') }}
      </p>
      <div class="flex justify-end">
        <NextButton
          v-if="current.kind === 'summon'"
          ruby
          :is-loading="responding"
          :label="t('TELETEL_TEAM_PRESENCE.DIALOG.RESPOND')"
          @click="respond"
        />
        <NextButton
          v-else
          slate
          :label="t('TELETEL_TEAM_PRESENCE.DIALOG.OK')"
          @click="close"
        />
      </div>
    </div>
  </div>
</template>
